import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/router";
import classnames from "classnames";

//Style
import Styles from "./ProfileItemPopupMenu.module.scss";

//Context
import { AppContext } from "@vahak/core/dist/app-context";

//component
import Dropdown from "@vahak/core/dist/components/dropdown/Dropdown";
import AvatarImage from "../../../utils/avatar-image/AvatarImage";
import Flex from "@vahak/core-ui/dist/layout/Flex";
import Typography from "@vahak/core-ui/dist/components/Typography";
import BottomSheet from "@vahak/core-ui/dist/components/BottomSheet/BottomSheet";
import Button from "@vahak/core-ui/dist/components/Button/Button";

//Constant
import COLORS from "@vahak/core-ui/dist/constants/colors";
import { IDs } from "@vahak/core/dist/constants/automation";

import { ROUTES } from "@vahak/core/dist/constants/routeStrings";

import { ADMIN_OPERATION } from "../../../../constants/adminOperation";

//methods
import { handleLogout } from "@vahak/core/dist/methods/handleLogout";
import { performLogoutAndRedirectHome } from "../../../login-form-new/hooks/performLogoutAndRedirectHome";
import IfCanAccess from "../../../admin/IfCanAccess/IfCanAccess";

//Hooks
import { useToggleModalWithLocalState } from "@vahak/core-ui/dist/hooks/useToggleModalWithLocalState";
import useMediaQuery from "@vahak/core-ui/dist/hooks/useMediaQuery";
import { useCsDashboard } from "@vahak/core/dist/hooks/useCsDashboard";

//Icon
import LogoutIcon from "@vahak/core/dist/standard-icons/Logout.svg";
import ProfileIcon from "@vahak/core/dist/standard-icons/profile.svg";
import Chip from "@vahak/core-ui/dist/components/Chip";
import { useClickToCall } from "../../../../context/call/ClickToCallAppProvider";
import { ALLOWED_ACTION_BY_ROLES } from "@vahak/core/dist/constants/authorizedUsers";
import CsCustomPrefernce from "../custom-prefernce/CsCustomPrefernce";
import StopPropagation from "../../../helper/StopPropagation";

interface ProfileDropDownProps {
    onClickMenuCallBack?: () => void;
    /** When true, opens profile in a bottom sheet (TopNav v2 mobile/tablet). */
    useBottomSheetMenu?: boolean;
}

const ProfileItemPopupMenu = ({ onClickMenuCallBack, useBottomSheetMenu }: ProfileDropDownProps) => {
    const clickToCallContext = useClickToCall();
    const {
        companyLogo,
        name,
        setCompanyId,
        setIsPhoneVerified,
        setName,
        setLang,
        setCompanyLogo,
        setUserId,
        phoneNum,
        setCsDashboardUserRoles,
        setCustomerCompanyId,
        setCustomerName,
        setCustomerPhone,
        csDashboardUserRoles,
        setSessionId,
        fromFlutterApp,
        setThirdPartyCompanyName
    } = useContext(AppContext);
    const {
        isModalOpen: isDropDownOpen,
        openModal: openProfilePopup,
        toggleModal: toggleProfilePopup
    } = useToggleModalWithLocalState(false);
    const {
        isModalOpen: isUserProfileSheetVisible,
        toggleModal: toggleUserProfileSheetPopup,
        closeModal: closeUserProfileSheetPopup
    } = useToggleModalWithLocalState(false);

    const preferncePopup = useToggleModalWithLocalState(false);
    const [isMounted, setIsMounted] = useState(false);

    const router = useRouter();

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        const closeSheetOnNavigate = () => {
            closeUserProfileSheetPopup();
        };

        router.events.on("routeChangeStart", closeSheetOnNavigate);
        return () => {
            router.events.off("routeChangeStart", closeSheetOnNavigate);
        };
    }, [closeUserProfileSheetPopup, router.events]);

    const isDesktopViewport = useMediaQuery({ queryType: "desktop" });
    const shouldUseBottomSheet = useBottomSheetMenu ?? !isDesktopViewport;

    const closeProfileMenu = () => {
        onClickMenuCallBack?.();
        if (shouldUseBottomSheet) {
            if (isUserProfileSheetVisible) {
                toggleUserProfileSheetPopup();
            }
            return;
        }
        if (isDropDownOpen) {
            toggleProfilePopup();
        }
    };

    const onClickLogout = async () => {
        closeProfileMenu();
        await performLogoutAndRedirectHome({
            router,
            setCompanyId,
            setIsPhoneVerified,
            setName,
            setLang,
            setCompanyLogo,
            setUserId,
            phoneNum: Number(phoneNum),
            setCsDashboardUserRoles,
            setCustomerCompanyId,
            setCustomerName,
            setCustomerPhone,
            setSessionId,
            setThirdPartyCompanyName
        });
    };

    const handleAdminConsoleLogout = () => {
        handleLogout({
            setCompanyId,
            setIsPhoneVerified,
            setName,
            setLang,
            setCompanyLogo,
            setUserId,
            setCsDashboardUserRoles,
            setCustomerCompanyId,
            setCustomerPhone,
            setCustomerName,
            setSessionId
        });
    };

    const PROFILE_OPTIONS = [
        {
            id: IDs.profileDropDown.myProfileBtn,
            title: "My Profile",
            route: ROUTES.userProfile.getUrl(),
            handler: undefined,
            icon: <ProfileIcon />,
            wrapperClass: "",
            textColor: undefined,
            hide: false
        },
        {
            id: IDs.profileDropDown.profileLogout,
            title: "Logout",
            route: "",
            handler: onClickLogout,
            icon: <LogoutIcon />,
            wrapperClass: Styles.logout,
            textColor: COLORS.RED,
            hide: undefined
        }
    ];
    const handleOptionClick = (route: string) => {
        closeProfileMenu();
        router.push(route, undefined, {
            shallow: true
        });
    };
    const handleOtherClick = (type: "prefernce") => {
        closeProfileMenu();
        if (type === "prefernce") {
            preferncePopup.openModal();
        }
    };

    const { isCsDashboardUser } = useCsDashboard();

    const renderOptions = () => {
        return !isCsDashboardUser ? (
            <Flex padding={20} flexDirection="column">
                <Flex alignItems="center" gap={10}>
                    <AvatarImage imageUrl={companyLogo} alt={name || "Vahak User"} size="sm" />
                    <Typography weight="semibold" size="m">
                        {name || "Guest"}
                    </Typography>
                </Flex>
                <Flex className={classnames(Styles.profileOptions)} flexDirection="column" gap={20}>
                    {PROFILE_OPTIONS.map((option) => {
                        if (option.hide) return;
                        return (
                            <Flex
                                alignItems="center"
                                gap={10}
                                className={classnames(Styles.profileOption, option.wrapperClass)}
                                onClick={() => (option.handler ? option.handler() : handleOptionClick(option.route))}
                                id={option.id}
                            >
                                {option.icon}
                                <Typography
                                    weight="semibold"
                                    size="m"
                                    color={option.textColor ? option.textColor : COLORS.BLACK}
                                >
                                    {option.title}
                                </Typography>
                            </Flex>
                        );
                    })}
                </Flex>
            </Flex>
        ) : (
            <Flex padding={20} flexDirection="column" gap={10}>
                <Flex gap={10}>
                    <AvatarImage imageUrl={companyLogo} alt={name || "Vahak User"} size="sm" />
                    <Flex gap={0} flexDirection="column">
                        <Typography weight="semibold" size="m">
                            {name}
                        </Typography>
                        <Typography weight="semibold" size="sm" color={COLORS.GREY_500}>
                            {phoneNum}
                        </Typography>
                        <Typography weight="semibold" size="sm" color={COLORS.GREY_500} textTransform="uppercase">
                            {csDashboardUserRoles?.map((role) => role.replaceAll("_", " ")).join(", ")}
                        </Typography>
                    </Flex>
                </Flex>

                <IfCanAccess action={ADMIN_OPERATION.profileView}>
                    <Flex>
                        <Chip
                            radius={100}
                            size="auto"
                            color="red"
                            filled
                            {...(clickToCallContext?.isReady && {
                                color: "green"
                            })}
                        >
                            <Typography>
                                Plivo Status : {clickToCallContext?.isReady ? "Connected" : "Not Connected"}
                            </Typography>
                        </Chip>
                    </Flex>
                </IfCanAccess>
                <Flex flexDirection="column" gap={20} padding={"20px 20px 0px 20px"}>
                    <IfCanAccess action={ADMIN_OPERATION.blockUnblockUser}>
                        <Flex alignItems="center" gap={10} className={classnames(Styles.profileOption)}>
                            <Button
                                fillType="text"
                                onClick={() => handleOptionClick(ROUTES.adminConsoleBlockUsers.getUrl())}
                            >
                                <Typography weight="semibold" size="sm" color={COLORS.BLACK}>
                                    Block Users
                                </Typography>
                            </Button>
                        </Flex>
                    </IfCanAccess>

                    <IfCanAccess action="" roles={ALLOWED_ACTION_BY_ROLES.CUSTOM_PREFERENCE_VISIBILTY}>
                        <Flex alignItems="center" gap={10} className={classnames(Styles.profileOption)}>
                            <Button fillType="text" onClick={() => handleOtherClick("prefernce")}>
                                <Typography weight="semibold" size="sm" color={COLORS.BLACK}>
                                    Custom Prefernce
                                </Typography>
                                <StopPropagation>
                                    <CsCustomPrefernce
                                        popupProps={{
                                            isOpen: preferncePopup?.isModalOpen,
                                            onToggle: preferncePopup?.toggleModal
                                        }}
                                    />
                                </StopPropagation>
                            </Button>
                        </Flex>
                    </IfCanAccess>

                    <IfCanAccess action={ADMIN_OPERATION.profileView}>
                        <Flex alignItems="center" gap={10} className={classnames(Styles.profileOption)}>
                            <Button
                                fillType="text"
                                onClick={() => handleOptionClick(ROUTES.adminConsoleSendWhatsAppMessage.getUrl())}
                            >
                                <Typography weight="semibold" size="sm" color={COLORS.BLACK}>
                                    Send Whatsapp Message
                                </Typography>
                            </Button>
                        </Flex>
                    </IfCanAccess>
                    <IfCanAccess action="" roles={ALLOWED_ACTION_BY_ROLES["ALLOW_QUICK_LOGOUT"]}>
                        {Boolean(fromFlutterApp) ? (
                            <></>
                        ) : (
                            <Flex
                                alignItems="center"
                                gap={10}
                                className={classnames(Styles.profileOption)}
                                onClick={handleAdminConsoleLogout}
                            >
                                <Button fillType="text" id={IDs.profileDropDown.profileLogout}>
                                    <Typography weight="semibold" size="sm" color={COLORS.RED}>
                                        Logout
                                    </Typography>
                                </Button>
                            </Flex>
                        )}
                    </IfCanAccess>
                </Flex>
            </Flex>
        );
    };

    return (
        <div className={Styles.main}>
            {!shouldUseBottomSheet ? (
                <Dropdown
                    isOpen={isDropDownOpen}
                    DropdownToggleBtn={<AvatarImage imageUrl={companyLogo} alt={name || "Vahak User"} size="sm" />}
                    handleToggle={toggleProfilePopup}
                    containerClassName={""}
                    direction="left"
                    dropdownClassName={Styles.profileDropdown}
                    dropdownToggleClassName={Styles.profileDropdownDropdownToggle}
                    id={IDs.header.profileAvatar}
                >
                    {renderOptions()}
                </Dropdown>
            ) : (
                <span id={IDs.header.profileAvatar}>
                    <AvatarImage
                        size="sm"
                        imageUrl={companyLogo}
                        alt={name || "Vahak User"}
                        onClick={toggleUserProfileSheetPopup}
                    />
                </span>
            )}

            {/* Bottom sheet */}

            {shouldUseBottomSheet && isUserProfileSheetVisible && isMounted
                ? createPortal(
                      <BottomSheet
                          isOpen={isUserProfileSheetVisible}
                          onToggle={toggleUserProfileSheetPopup}
                          showCloseIcon={false}
                          showHeader={false}
                          bodyMinHeight="auto"
                      >
                          {renderOptions()}
                      </BottomSheet>,
                      document.body
                  )
                : null}
        </div>
    );
};

export default ProfileItemPopupMenu;
